import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import Permissioned from 'dashboard/components/Permissioned';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { Role } from 'dashboard/models/role';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import EditTeamForm, { FormEditTeamData } from 'dashboard/views/DashboardTeamShowView/EditTeamForm';
import TeamDetails from 'dashboard/views/DashboardTeamShowView/TeamDetails';
import { Permission } from 'lib/permissions';

export interface DashboardTeamShowViewProps {
  isloading: boolean;
  allRoles: Role[];
  team: Team;
  teamUserIds: string[];
  users: User[];
  deleteTeam: (teamId: string) => Promise<void>;
  updateTeam: (team: FormEditTeamData) => Promise<void>;
}

const DashboardTeamShowView = ({
  isloading,
  allRoles,
  team,
  teamUserIds,
  users,
  deleteTeam,
  updateTeam,
}: DashboardTeamShowViewProps) => {
  if (isloading) {
    return <LoadingIndicator />;
  }

  if (!team || team.attributes.status !== 'active') {
    return (
      <Backdrop>
        <NullState message='We couldn’t find that Team.' backRoute='/dashboard/teams' />
      </Backdrop>
    );
  }

  return (
    <TeamDetailsView
      allRoles={allRoles}
      team={team}
      teamUserIds={teamUserIds}
      users={users}
      deleteTeam={deleteTeam}
      updateTeam={updateTeam}
    />
  );
};

interface TeamDetailsViewProps {
  allRoles: Role[];
  team: Team;
  teamUserIds: string[];
  users: User[];
  deleteTeam: (teamId: string) => Promise<void>;
  updateTeam: (team: FormEditTeamData) => Promise<void>;
}

const TeamDetailsView = ({
  allRoles,
  team,
  teamUserIds,
  users,
  deleteTeam,
  updateTeam,
}: TeamDetailsViewProps) => {
  const userIdToRoleNames = useMemo(() => {
    return users.reduce(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (map, user) => {
        const roleNames = user.relationships.roles.data.map(
          (userRole) => allRoles.find((role) => role.id === userRole.id)?.attributes?.name,
        );
        return {
          ...map,
          [user.id]: roleNames,
        };
      },
      {} as Record<string, string[]>,
    );
  }, [users, allRoles]);

  return (
    <>
      <Helmet title={`${team.attributes.name} - Teams`} />
      <Permissioned
        permission={Permission.TeamWrite}
        fallback={
          <TeamDetails
            team={team}
            teamUserIds={teamUserIds}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            userIdToRoleNames={userIdToRoleNames}
            users={users}
          />
        }
      >
        <EditTeamForm
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          userIdToRoleNames={userIdToRoleNames}
          team={team}
          teamUserIds={teamUserIds}
          users={users}
          deleteTeam={deleteTeam}
          updateTeam={updateTeam}
        />
      </Permissioned>
    </>
  );
};

export default DashboardTeamShowView;
